/**
 * Create entity — /settings/custom-modules/$key/entities/new
 *
 * Form for creating a new entity: key/name/description + the
 * FieldsEditor for the fields array. On submit calls
 * modules.custom.entities.create and navigates to the entity editor.
 */

import type { FieldDefinition } from '@cloud/custom-modules'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { FieldsEditor } from '@/components/custom/fields-editor'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/entities/new')({
  component: NewEntityPage,
})

function NewEntityPage() {
  const { key: moduleKey } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [key, setKey] = useState('')
  const [keyDirty, setKeyDirty] = useState(false)
  const [name, setName] = useState('')
  const [namePlural, setNamePlural] = useState('')
  const [description, setDescription] = useState('')
  const [fields, setFields] = useState<FieldDefinition[]>([])
  const [primaryDisplayField, setPrimaryDisplayField] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const createMutation = useMutation(
    orpc.modules.custom.entities.create.mutationOptions({
      onSuccess: (entity) => {
        toast.success(`Entity "${entity.name}" created`)
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.entities.list.queryKey({
            input: { moduleKey },
          }),
        })
        void navigate({
          to: '/settings/custom-modules/$key/entities/$entityKey',
          params: { key: moduleKey, entityKey: entity.key },
        })
      },
      onError: (err) => setFormError(err.message || 'Failed to create entity'),
    }),
  )

  const handleNameChange = (value: string) => {
    setName(value)
    if (!keyDirty) {
      const suggested = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 63)
      setKey(suggested)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    createMutation.mutate({
      moduleKey,
      key,
      name,
      namePlural: namePlural || undefined,
      description: description || undefined,
      fields,
      primaryDisplayField: primaryDisplayField || undefined,
    })
  }

  return (
    <div className="space-y-4">
      <Link
        to="/settings/custom-modules/$key/entities"
        params={{ key: moduleKey }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to entities
      </Link>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>New entity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Audit"
                  required
                />
              </div>
              <div>
                <Label htmlFor="namePlural">Plural name</Label>
                <Input
                  id="namePlural"
                  value={namePlural}
                  onChange={(e) => setNamePlural(e.target.value)}
                  placeholder="Audits"
                />
              </div>
              <div>
                <Label htmlFor="key">
                  Key <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="key"
                  value={key}
                  onChange={(e) => {
                    setKey(e.target.value)
                    setKeyDirty(true)
                  }}
                  placeholder="audit"
                  pattern="[a-z][a-z0-9_]*"
                  required
                />
              </div>
              <div>
                <Label htmlFor="primary">Primary display field</Label>
                <Select
                  value={primaryDisplayField || '__none__'}
                  onValueChange={(v) =>
                    setPrimaryDisplayField(v === '__none__' ? '' : v)
                  }
                >
                  <SelectTrigger id="primary">
                    <SelectValue placeholder="— auto —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— auto —</SelectItem>
                    {fields.map((f) => (
                      <SelectItem key={f.key} value={f.key}>
                        {f.label || f.key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Field shown as the row title in lists and relation pickers.
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this entity represent?"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fields</CardTitle>
          </CardHeader>
          <CardContent>
            <FieldsEditor fields={fields} onChange={setFields} />
          </CardContent>
        </Card>

        {formError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {formError}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Link
            to="/settings/custom-modules/$key/entities"
            params={{ key: moduleKey }}
          >
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create entity'}
          </Button>
        </div>
      </form>
    </div>
  )
}
